import {
	timeParse,
	timeFormat,
	utcMonday,
	utcFormat,
	utcYear,
	utcMonths,
	utcMonth,
	scaleSequential,
	interpolateOrRd,
	nest,
	create,
	range
} from "d3";
import { tileLayer, map, polygon } from "leaflet";
import "leaflet/dist/leaflet.css";

const nowYear = new Date().getFullYear();

const isLeap = year => year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0);

const ofLength = len => Array.apply(null, new Array(len));

const parseDayoYear = timeParse("%Y %j");
const verifyDayOYear = timeFormat("%Y %j");
const parseRemote = timeParse("%Y-%m-%d");

function clearMap() {
	let el = document.querySelector("#map");
	if (el) {
		const newEl = document.createElement("div");
		newEl.setAttribute("id", "map");
		el.replaceWith(newEl);
		el = newEl;
	}
	document.querySelector("#graph").innerHTML = "";
	return el;
}

function renderBbox(center, bbox) {
	const el = clearMap();
	const myMap = map(el).setView(center, 13);
	const osm = tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
		maxZoom: 19,
		attribution:
			'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
	}).addTo(myMap);

	setTimeout(() => {
		const box = polygon(bbox).addTo(myMap);
		myMap.fitBounds(box.getBounds(), { padding: [10, 10] });
		myMap.invalidateSize();
	});
	return true;
}

function bboxParse(str) {
	str = str.match(/\(\(([^\)]+)/)[1];
	return str
		.split(",")
		.map(pair => pair.trim().split(" "))
		.map(([a, b]) => [parseFloat(b, 10), parseFloat(a, 10)]);
}

let box = {};

async function getYear(year, lnglat) {
	let days = isLeap(year) ? 366 : 365;
	if (year === nowYear) {
		days = parseInt(verifyDayOYear(new Date()).split(" ")[1], 10);
	}
	const withLightning = {};
	await fetch(
		`https://www.ncdc.noaa.gov/swdiws/json/nldn/${year}0101:${year +
			1}0101/?stat=tilesum:${lnglat}`
	)
		.then(res => res.json())
		.then(({ result }) =>
			result.forEach(val => {
				if (!box[lnglat]) {
					box[lnglat] = renderBbox(
						[parseFloat(val.CENTERLAT), parseFloat(val.CENTERLON)],
						bboxParse(val.SHAPE)
					);
				}
				const date = parseRemote(val.DAY);
				withLightning[verifyDayOYear(date)] = {
					date,
					value: val.FCOUNT
				};
			})
		);
	return ofLength(days).map((_, i) => {
		const date = parseDayoYear(`${year} ${i + 1}`);
		const exists = withLightning[verifyDayOYear(date)];
		return {
			date,
			value: exists ? exists.value : 0
		};
	});
}

async function render(lnglat) {
	const cellSize = 17;
	const width = 964;
	const height = cellSize * 9;
	const timeWeek = utcMonday;
	const countDay = d => (d.getUTCDay() + 6) % 7;

	function pathMonth(t) {
		const n = 7;
		const d = Math.max(0, Math.min(n, countDay(t)));
		const w = timeWeek.count(utcYear(t), t);
		return `${
			d === 0
				? `M${w * cellSize},0`
				: d === n
				? `M${(w + 1) * cellSize},0`
				: `M${(w + 1) * cellSize},0V${d * cellSize}H${w * cellSize}`
		}V${n * cellSize}`;
	}
	const formatDate = utcFormat("%x");
	const formatDay = d => "SMTWTFS"[d.getUTCDay()];
	const formatMonth = utcFormat("%b");
	const color = scaleSequential(interpolateOrRd).domain([0, 10]);
	const data = await Promise.all(
		ofLength(5).map((_, i) => getYear(nowYear - i, lnglat))
	).then(results =>
		results
			.reduce((all, year) => all.concat(year), [])
			.sort((a, b) => a.date - b.date)
	);
	const years = nest()
		.key(d => d.date.getUTCFullYear())
		.entries(data)
		.reverse();

	const svg = create("svg")
		.attr("viewBox", [0, 0, width, height * years.length])
		.style("font", "10px sans-serif")
		.style("width", "100%")
		.style("height", "auto");

	const year = svg
		.selectAll("g")
		.data(years)
		.join("g")
		.attr(
			"transform",
			(d, i) => `translate(40,${height * i + cellSize * 1.5})`
		);

	year
		.append("text")
		.attr("x", -5)
		.attr("y", -5)
		.attr("font-weight", "bold")
		.attr("text-anchor", "end")
		.text(d => d.key);

	year
		.append("g")
		.attr("text-anchor", "end")
		.selectAll("text")
		.data(range(7).map(i => new Date(1995, 0, i)))
		.join("text")
		.attr("x", -5)
		.attr("y", d => (countDay(d) + 0.5) * cellSize)
		.attr("dy", "0.31em")
		.text(formatDay);

	const days = year
		.append("g")
		.selectAll("g")
		.data(d => d.values)
		.join("g");

	days
		.append("rect")
		.attr("x", d => timeWeek.count(utcYear(d.date), d.date) * cellSize + 0.5)
		.attr("y", d => countDay(d.date) * cellSize + 0.5)
		.attr("width", cellSize - 1)
		.attr("height", cellSize - 1)
		.attr("fill", d => color(d.value))
		.append("title")
		.text(d => `${formatDate(d.date)}: ${d.value}`);

	days
		.append("text")
		.text(d => (d.value ? d.value : ""))
		.attr("x", d => timeWeek.count(utcYear(d.date), d.date) * cellSize + 0.5)
		.attr("y", d => countDay(d.date) * cellSize + 0.5)
		.attr("width", cellSize - 1)
		.attr("height", cellSize - 1)
		.attr("text-anchor", "middle")
		.attr("fill", "#FFF")
		.attr("dy", "11px")
		.attr("dx", "8px");

	const month = year
		.append("g")
		.selectAll("g")
		.data(d =>
			utcMonths(utcMonth(d.values[0].date), d.values[d.values.length - 1].date)
		)
		.join("g");

	month
		.filter((d, i) => i)
		.append("path")
		.attr("fill", "none")
		.attr("stroke", "#fff")
		.attr("stroke-width", 3)
		.attr("d", pathMonth);

	month
		.append("text")
		.attr("x", d => timeWeek.count(utcYear(d), timeWeek.ceil(d)) * cellSize + 2)
		.attr("y", -5)
		.text(formatMonth);

	const svgel = svg.node();
	svgel.setAttribute("id", "graph");
	document.querySelector("#graph").replaceWith(svgel);
}

function check() {
	const params = new URLSearchParams(window.location.search);
	const lnglat = params.get("lnglat");
	const name = params.get("name");
	if (lnglat) {
		document.querySelector(
			"#place"
		).textContent = `the past 5 years of lightning in ${name || lnglat}`;
		render(lnglat);
	}
}

function show(lnglat, name = lnglat) {
	history.pushState(
		{},
		undefined,
		`?lnglat=${lnglat}&name=${encodeURIComponent(name)}`
	);
	check();
}

async function search(input) {
	clearMap();
	if (input.match(/^(\-?\d+(\.\d+)?),\s*(\-?\d+(\.\d+)?)$/)) {
		return show(input);
	}
	fetch(`https://nominatim.openstreetmap.org/search/${input}?format=json`)
		.then(res => res.json())
		.then(json => {
			if (json.length) {
				const { lat, lon, display_name } = json[0];
				show(`${lon},${lat}`, display_name);
			}
		});
}

document.querySelector("#search").addEventListener("submit", e => {
	e.preventDefault();
	search(e.target.elements.namedItem("search").value);
});
window.addEventListener("load", check);
